import { useCallback, useEffect, useMemo, useState, ChangeEvent, KeyboardEvent } from 'react';
import {
  Box,
  Button,
  TablePagination,
  TablePaginationProps,
  TextField,
  Typography,
} from '@mui/material';

type TeamsPaginationFooterProps = {
  paginationProps: TablePaginationProps & { indexBase?: 0 | 1 };
};

export const TeamsPaginationFooter = ({ paginationProps }: TeamsPaginationFooterProps) => {
  const { indexBase = 0, page, onPageChange, count, rowsPerPage, ...rest } = paginationProps;
  const displayPage = useMemo(() => Math.max(1, page - indexBase + 1), [page, indexBase]);
  const [inputValue, setInputValue] = useState<string>(() => String(displayPage));

  useEffect(() => {
    setInputValue(String(displayPage));
  }, [displayPage]);

  const totalPages = useMemo(() => {
    if (typeof rowsPerPage !== 'number' || rowsPerPage <= 0) {
      return undefined;
    }
    if (typeof count !== 'number' || count < 0) {
      return undefined;
    }
    return Math.max(1, Math.ceil(count / rowsPerPage));
  }, [count, rowsPerPage]);

  const handleMuiPageChange: TablePaginationProps['onPageChange'] = useCallback(
    (event, newMuiPage) => {
      const logicalPage = newMuiPage + indexBase;
      onPageChange?.(event, logicalPage);
    },
    [indexBase, onPageChange],
  );

  const handleJump = useCallback(() => {
    if (!onPageChange) return;
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return;
    const floored = Math.floor(parsed);
    const minimumDisplayPage = 1;
    const maximumDisplayPage = totalPages ?? Math.max(floored, minimumDisplayPage);
    const clampedDisplay = Math.min(Math.max(floored, minimumDisplayPage), maximumDisplayPage);
    const nextLogicalPage = indexBase + clampedDisplay - 1;
    if (nextLogicalPage === page) {
      setInputValue(String(clampedDisplay));
      return;
    }
    onPageChange(null as any, nextLogicalPage);
    setInputValue(String(clampedDisplay));
  }, [indexBase, inputValue, onPageChange, page, totalPages]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleJump();
      }
    },
    [handleJump],
  );

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = event.target.value.replace(/[^\d]/g, '');
    setInputValue(digitsOnly === '' ? '1' : digitsOnly);
  }, []);

  const muiPage = Math.max(0, page - indexBase);
  // Display total pages for the "Jump to page" feature
  const displayTotalPages = totalPages ?? '-';

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
      <Box flex="1 1 auto" minWidth={0}>
        <TablePagination
          component="div"
          {...rest}
          count={count}
          rowsPerPage={rowsPerPage}
          page={muiPage}
          onPageChange={handleMuiPageChange}
        />
      </Box>
      <Box display="flex" alignItems="center" sx={{ ml: 2, flexShrink: 0 }}>
        <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
          Jump to page
        </Typography>
        <TextField
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          variant="outlined"
          size="small"
          type="number"
          inputProps={{ min: 1, step: 1 }}
          sx={{ width: 72, '& input': { textAlign: 'center' } }}
        />
        <Typography variant="body2" color="textSecondary" sx={{ mx: 1 }}>
          / {displayTotalPages}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={!inputValue}
          onClick={handleJump}
        >
          Go
        </Button>
      </Box>
    </Box>
  );
};

export default TeamsPaginationFooter;
